import {
  CUSTOM_LINK_FIELDS,
  DEFAULT_CROSS_SELL_RADIUS,
  PROPERTY_ID,
  SOCIAL_MEDIA_TYPES,
} from '../constants';
import { DAYS_OF_THE_WEEK } from '../PropertyTabs/TabViews/Overview/Hours/HoursList/constants';
import { HOURS_TABS } from '../PropertyTabs/TabViews/Overview/Hours/constants';
import {
  ADVANCED,
  AFFORDABLE,
  AFFORDABLE_OVERVIEW_FIELDS,
  CROSS_SELL,
  CROSS_SELL_FIELDS,
  FEATURES,
  INCOME_ELIGIBILITY,
  INCOME_ELIGIBILITY_FIELDS,
  HOURS,
  MARKETING_MEDIA,
  MARKETING_MEDIA_FIELDS,
  OVERVIEW,
  PROPERTY_DETAILS,
  PROPERTY_DETAILS_FIELDS,
} from './constants';

const {
  CROSS_SELL_RADIUS,
  CROSS_SELL_REFERRAL_SOURCE,
  NEARBY_PROPERTIES_EXCLUDED_PROPERTIES,
} = CROSS_SELL_FIELDS;

const GUEST_CARD_REQUIREMENTS_SNAKE_TO_CAMEL = {
  require_lease_term: 'requireLeaseTerm',
  require_preferred_bedroom_count: 'requirePreferredBedroomCount',
  require_preferred_unit: 'requirePreferredUnit',
  require_prospect_floorplan: 'requireProspectFloorplan',
  require_prospect_move_in_date: 'requireProspectMoveInDate',
};

const GUEST_CARD_REQUIREMENTS_CAMEL_TO_SNAKE = Object.fromEntries(
  Object.entries(GUEST_CARD_REQUIREMENTS_SNAKE_TO_CAMEL).map(
    ([snake, camel]) => [camel, snake],
  ),
);

const normalizeCrossSellProperties = (properties) =>
  properties.reduce((propertyList, property) => {
    propertyList.push({
      distance: property.distance || 0,
      propertyId: property.id,
      propertyName: property.name || '',
      unit: property.unit || '',
    });

    return propertyList;
  }, []);

const getReferralSourceOptions = (propertySourceData) => {
  const referralSourceOptions = propertySourceData.reduce(
    (propertySources, propertySource) => {
      if (propertySource.is_enabled) {
        propertySources.push({
          parentSourceId: propertySource.parent_source_id,
          propertySourceId: propertySource.property_source_id,
          label: propertySource.name,
          value: propertySource.name,
        });
      }
      return propertySources;
    },
    [],
  );

  referralSourceOptions.sort((optionA, optionB) =>
    optionA.label.localeCompare(optionB.label, 'en', {
      sensitivity: 'base',
    }),
  );

  return referralSourceOptions;
};

export const normalizeAdvancedSettings = (
  advancedSettings = {},
  propertyId,
) => {
  const { cross_sell: crossSell = {}, preferences = {} } = advancedSettings;

  const {
    nearby_properties_excluded_properties: nearbyPropertiesExcludedProperties,
    properties,
    property_source_data = [],
  } = crossSell;

  const crossSellRadiusMap = crossSell.cross_sell_radius || {};

  return {
    [CROSS_SELL]: {
      [CROSS_SELL_RADIUS]:
        crossSellRadiusMap[propertyId] ?? DEFAULT_CROSS_SELL_RADIUS,
      [CROSS_SELL_REFERRAL_SOURCE]: crossSell.cross_sell_referral_source || '',
      [NEARBY_PROPERTIES_EXCLUDED_PROPERTIES]:
        nearbyPropertiesExcludedProperties || [],
      properties: normalizeCrossSellProperties(properties || []),
      referralSourceOptions: getReferralSourceOptions(property_source_data),
    },
    guestCardRequirements: Object.fromEntries(
      Object.entries(GUEST_CARD_REQUIREMENTS_SNAKE_TO_CAMEL).map(
        ([snakeKey, camelKey]) => [camelKey, Boolean(preferences[snakeKey])],
      ),
    ),
    selfGuidedTours: {
      selfGuidedTourButtonLabel:
        preferences.self_guided_tour_button_label || '',
      selfGuidedTourUrl: preferences.self_guided_tour_url || '',
      toursExportOnlyFavoriteUnit:
        preferences.tours_export_only_favorite_unit ?? false,
    },
  };
};

export const normalizeAffordable = (affordableHousing = {}) => {
  const {
    details = '',
    enabled = true,
    income_eligibility: incomeEligibility = [],
  } = affordableHousing;

  return {
    [AFFORDABLE_OVERVIEW_FIELDS.DETAILS]: details || '',
    enabled,
    [INCOME_ELIGIBILITY]: incomeEligibility.map(
      ({ maximum_income: maximumIncome, occupants }) => ({
        [INCOME_ELIGIBILITY_FIELDS.MAXIMUM_INCOME]: maximumIncome,
        [INCOME_ELIGIBILITY_FIELDS.OCCUPANTS]: occupants,
      }),
    ),
  };
};

export const normalizeHours = (hours = []) =>
  hours.reduce((weeklyHours, day) => {
    const weekDay = DAYS_OF_THE_WEEK[day.weekday - 1];

    weeklyHours[weekDay] = {
      closesAt: day.end_time,
      day: day.weekday,
      isActive: true,
      maxVisitors: day.max_visitors,
      opensAt: day.start_time,
    };
    return weeklyHours;
  }, {});

export const normalizePropertyHours = (propertyHours = {}) => ({
  [HOURS_TABS.OFFICE]: normalizeHours(propertyHours.doorway),
  [HOURS_TABS.TOURING]: normalizeHours(propertyHours.touring),
  [HOURS_TABS.KNOCKBOT]: normalizeHours(propertyHours.self_guided),
});

export const normalizeCustomLinks = (customLinks, videoUrl) => {
  const normalizedCustomLinks =
    customLinks?.map((customLink) => ({
      [CUSTOM_LINK_FIELDS.TITLE]: customLink.title,
      [CUSTOM_LINK_FIELDS.URL]: customLink.url,
    })) || [];

  if (videoUrl) {
    normalizedCustomLinks.push({
      [CUSTOM_LINK_FIELDS.TITLE]: 'video',
      [CUSTOM_LINK_FIELDS.URL]: videoUrl,
    });
  }

  return normalizedCustomLinks;
};

export const normalizeOverview = (overview = {}) => {
  const {
    contact_info: contactInfo = {},
    features = {
      community: [],
      location: [],
      notes: '',
      units: [],
    },
    hours = {},
    marketing_media: {
      brand_color: brandColor = '',
      photos = [],
      logos = [],
      social = {},
    } = {},
    property_details: {
      address = {},
      description = '',
      property_name,
      total_units,
      unit_types = [],
      year_built,
    } = {},
  } = overview;

  return {
    contactInfo: {
      knockEmail: contactInfo.knock_email || '',
      owner: contactInfo.owner || '',
    },
    [FEATURES]: {
      community: features.community || [],
      location: features.location || [],
      notes: features.notes || '',
      units: features.units || [],
    },
    [HOURS]: normalizePropertyHours(hours),
    [MARKETING_MEDIA]: {
      [MARKETING_MEDIA_FIELDS.BRAND_COLOR]: brandColor || '',
      [MARKETING_MEDIA_FIELDS.LOGOS]: logos || [],
      [MARKETING_MEDIA_FIELDS.PHOTOS]: photos || [],
      [MARKETING_MEDIA_FIELDS.PROPERTY_WEBSITE]: social.website || '',
      [MARKETING_MEDIA_FIELDS.SOCIAL_MEDIA]: {
        [SOCIAL_MEDIA_TYPES.FACEBOOK]: social.facebook || '',
        [SOCIAL_MEDIA_TYPES.INSTAGRAM]: social.instagram || '',
        [SOCIAL_MEDIA_TYPES.X]: social.x || '',
        [SOCIAL_MEDIA_TYPES.YELP]: social.yelp || '',
      },
      [MARKETING_MEDIA_FIELDS.CUSTOM_LINKS]: normalizeCustomLinks(
        social.custom_links,
        social.video,
      ),
    },
    [PROPERTY_DETAILS]: {
      [PROPERTY_DETAILS_FIELDS.CITY]: address.city || '',
      [PROPERTY_DETAILS_FIELDS.DESCRIPTION]: description || '',
      [PROPERTY_DETAILS_FIELDS.NEIGHBORHOOD]: address.neighborhood || '',
      [PROPERTY_DETAILS_FIELDS.PROPERTY_NAME]: property_name || '',
      [PROPERTY_DETAILS_FIELDS.STATE]: address.state || '',
      [PROPERTY_DETAILS_FIELDS.STREET]: address.street || '',
      [PROPERTY_DETAILS_FIELDS.TOTAL_UNITS]: total_units || 0,
      [PROPERTY_DETAILS_FIELDS.UNIT_TYPES]:
        // TODO: remove unit_types hack when backend returns an array instead the
        // "#NEED TO FIGURE OUT WHERE THIS ONE LIVES" string
        unit_types.map?.((unitType) => unitType) || [],
      [PROPERTY_DETAILS_FIELDS.YEAR_BUILT]: String(year_built || ''),
      [PROPERTY_DETAILS_FIELDS.ZIP]: address.zip || '',
    },
  };
};

export const normalizeProperty = (property) => {
  return {
    [AFFORDABLE]: normalizeAffordable(property.affordable_housing),
    [ADVANCED]: normalizeAdvancedSettings(
      property.advanced_settings,
      property.id,
    ),
    amenities: {},
    leasing: {},
    [OVERVIEW]: normalizeOverview(property.overview),
    [PROPERTY_ID]: property.id,
  };
};

export const transformEntries = (entries) => {
  const filteredEntries = entries.filter(([_, value]) => value !== undefined);

  return Object.fromEntries(filteredEntries);
};

export const transformAddress = (propertyDetails) => {
  const addressEntries = [
    ['city', propertyDetails[PROPERTY_DETAILS_FIELDS.CITY]],
    ['neighborhood', propertyDetails[PROPERTY_DETAILS_FIELDS.NEIGHBORHOOD]],
    ['state', propertyDetails[PROPERTY_DETAILS_FIELDS.STATE]],
    ['street', propertyDetails[PROPERTY_DETAILS_FIELDS.STREET]],
    ['zip', propertyDetails[PROPERTY_DETAILS_FIELDS.ZIP]],
  ];

  const address = transformEntries(addressEntries);

  return Object.keys(address).length ? { address } : {};
};

export const transformPropertyDetails = (propertyDetails) => {
  if (!propertyDetails) {
    return {};
  }

  const address = transformAddress(propertyDetails);

  const otherEntries = [
    ['description', propertyDetails[PROPERTY_DETAILS_FIELDS.DESCRIPTION]],
    ['property_name', propertyDetails[PROPERTY_DETAILS_FIELDS.PROPERTY_NAME]],
    ['total_units', propertyDetails[PROPERTY_DETAILS_FIELDS.TOTAL_UNITS]],
    ['unit_types', propertyDetails[PROPERTY_DETAILS_FIELDS.UNIT_TYPES]],
    ['year_built', propertyDetails[PROPERTY_DETAILS_FIELDS.YEAR_BUILT]],
  ];

  const otherPropertyDetails = transformEntries(otherEntries);
  const allPropertyDetails = {
    ...address,
    ...otherPropertyDetails,
  };

  return Object.keys(allPropertyDetails).length
    ? { property_details: allPropertyDetails }
    : {};
};

export const transformMarketingMedia = (marketingMedia) => {
  if (!marketingMedia) {
    return {};
  }

  const marketingMediaEntries = [
    ['brand_color', marketingMedia[MARKETING_MEDIA_FIELDS.BRAND_COLOR]],
    ['logos', marketingMedia[MARKETING_MEDIA_FIELDS.LOGOS]],
    ['photos', marketingMedia[MARKETING_MEDIA_FIELDS.PHOTOS]],
  ];

  const transformedMarketingMedia = transformEntries(marketingMediaEntries);
  return Object.keys(transformedMarketingMedia).length
    ? { marketing_media: transformedMarketingMedia }
    : {};
};

export const transformNotesFeatures = (notesFeatures) => {
  if (!notesFeatures) {
    return {};
  }
  const notesFeaturesEntries = [
    ['community', notesFeatures.community],
    ['location', notesFeatures.location],
    ['notes', notesFeatures.notes],
    ['units', notesFeatures.units],
  ];
  const normalizedNotesFeatures = transformEntries(notesFeaturesEntries);
  return Object.keys(normalizedNotesFeatures).length
    ? { [FEATURES]: normalizedNotesFeatures }
    : {};
};

export const transformOverview = (overview) => {
  if (!overview) {
    return {};
  }

  const propertyDetails = transformPropertyDetails(overview.propertyDetails);
  const marketingMedia = transformMarketingMedia(overview.marketingMedia);
  const notesFeatures = transformNotesFeatures(overview[FEATURES]);

  return {
    overview: {
      ...marketingMedia,
      ...propertyDetails,
      ...notesFeatures,
    },
  };
};

const transformGuestCardRequirementsToSnakeCase = (guestCardRequirements) =>
  Object.fromEntries(
    Object.entries(guestCardRequirements || {}).map(([camelKey, value]) => [
      GUEST_CARD_REQUIREMENTS_CAMEL_TO_SNAKE[camelKey] ?? camelKey,
      value,
    ]),
  );

const transformSelfGuidedToursToSnakeCase = (selfGuidedTours = {}) => {
  const selfGuidedToursEntries = [
    [
      'self_guided_tour_button_label',
      selfGuidedTours.selfGuidedTourButtonLabel,
    ],
    ['self_guided_tour_url', selfGuidedTours.selfGuidedTourUrl],
    [
      'tours_export_only_favorite_unit',
      selfGuidedTours.toursExportOnlyFavoriteUnit,
    ],
  ];

  return transformEntries(selfGuidedToursEntries);
};

export const transformAdvancedSettings = (advancedSettings) => {
  if (!advancedSettings) {
    return {};
  }
  const guestCardPreferences = transformGuestCardRequirementsToSnakeCase(
    advancedSettings.guestCardRequirements,
  );
  const selfGuidedPreferences = transformSelfGuidedToursToSnakeCase(
    advancedSettings.selfGuidedTours,
  );
  const preferences = {
    ...guestCardPreferences,
    ...selfGuidedPreferences,
  };
  if (Object.keys(preferences).length === 0) {
    return {};
  }
  return { [ADVANCED]: { preferences } };
};

export const transformAffordable = (affordable) => {
  if (!affordable) {
    return {};
  }

  const entries = [
    ['details', affordable[AFFORDABLE_OVERVIEW_FIELDS.DETAILS]],
    [
      'income_eligibility',
      affordable[INCOME_ELIGIBILITY]?.map((row) => ({
        maximum_income: row[INCOME_ELIGIBILITY_FIELDS.MAXIMUM_INCOME],
        occupants: row[INCOME_ELIGIBILITY_FIELDS.OCCUPANTS],
      })),
    ],
  ];

  const transformed = transformEntries(entries);
  return Object.keys(transformed).length
    ? { affordable_housing: transformed }
    : {};
};

export const transformProperty = (propertyUpdates) => {
  const overview = transformOverview(propertyUpdates.overview);
  const advancedSettings = transformAdvancedSettings(
    propertyUpdates.advanced_settings,
  );
  const affordable = transformAffordable(propertyUpdates.affordable);

  return {
    ...overview,
    ...advancedSettings,
    ...affordable,
  };
};

export const transformCreateProperty = (propertyData) => {
  const hasLogo = Boolean(propertyData.logos?.length);
  return {
    community: {
      leasing: {
        terms: {},
        application: {},
      },
      location: {
        name: propertyData.propertyName,
        address: {
          ...(propertyData.propertyAddressLine2 && {
            address_line_2: propertyData.propertyAddressLine2,
          }),
          city: propertyData.propertyCity,
          house: '',
          street: propertyData.propertyAddress,
          state: propertyData.propertyState,
          zip: propertyData.propertyZip,
        },
      },
      social: {
        knock_email: propertyData.knockEmail,
        website: propertyData.propertyWebsite,
      },
      ...(hasLogo && { logos: propertyData.logos }),
    },
    owning_manager_id: propertyData.propertyOwner,
  };
};
